import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';

const StatCard = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-white/70 border border-neutral-200/60 rounded-xl px-6 py-6">
    <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">{label}</div>
    <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-4xl leading-none text-neutral-900">
      {value ?? 0}
    </div>
    {sub && <div className="mt-3 text-[12px] text-neutral-500">{sub}</div>}
  </div>
);

const HBar = ({ label, count, max }) => {
  const pct = max > 0 ? Math.max(4, (count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="w-40 shrink-0 text-[12px] text-neutral-700 truncate" title={label}>{label}</div>
      <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#C8A15A]" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-10 text-right text-[12px] text-neutral-500 tabular-nums">{count}</div>
    </div>
  );
};

const GrowthChart = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="flex items-end gap-2 h-32" data-testid="growth-chart">
      {series.map((p, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-2">
          <div
            className="w-full bg-[#C8A15A]/70 hover:bg-[#C8A15A] transition-colors rounded-t"
            style={{ height: `${(p.count / max) * 100}%`, minHeight: p.count > 0 ? '4px' : '2px' }}
            title={`${p.label}: ${p.count}`}
          />
          <div className="text-[10px] text-neutral-500 whitespace-nowrap">{p.label}</div>
        </div>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.get('/admin/analytics')
      .then((r) => setData(r.data))
      .catch(() => setError('Unable to load analytics.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="uppercase tracking-[0.24em] text-[11px] text-neutral-500">Loading analytics…</div>;
  if (error) return <div className="text-red-700">{error}</div>;

  const s = data.subscribers;
  const q = data.inquiries;
  const w = data.waitlist;
  const e = data.editions;

  const gaLink = GA4_ID
    ? `https://analytics.google.com/analytics/web/#/p${GA4_ID.replace('G-', '')}/reports/intelligenthome`
    : 'https://analytics.google.com';

  return (
    <div data-testid="admin-analytics-view">
      <header className="mb-10">
        <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Analytics</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">
          Website performance
        </h1>
        <p className="mt-3 text-[13px] text-neutral-500 max-w-lg leading-relaxed">
          First-party KPIs sourced directly from your database — subscribers, inquiries, and conversions. Traffic, sessions, and referral data live in Google Analytics.
        </p>
      </header>

      {/* GA4 traffic card */}
      <section className="mb-6">
        <div className="bg-white/70 border border-neutral-200/60 rounded-xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">Traffic Analytics</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-xl text-neutral-900">
              {GA4_ID ? 'Google Analytics 4' : 'Not connected yet'}
            </div>
            <p className="mt-2 text-[13px] text-neutral-500 max-w-xl">
              {GA4_ID
                ? `Tracking is live. Measurement ID ${GA4_ID}. Open GA4 to view visitors, sessions, page views, referrers, geography, and device breakdowns in real time.`
                : "Set REACT_APP_GA4_MEASUREMENT_ID in your frontend env to a value like G-XXXXXXXXXX to activate visitor tracking. Details in the deployment guide."}
            </p>
          </div>
          <a
            href={gaLink}
            target="_blank"
            rel="noreferrer"
            data-testid="ga4-open"
            className="shrink-0 bg-neutral-900 text-white uppercase tracking-[0.22em] text-[10px] px-5 py-3 rounded-full hover:bg-[#1a1a1a] whitespace-nowrap"
          >
            Open Google Analytics ↗
          </a>
        </div>
      </section>

      {/* Campaign Builder shortcut */}
      <section className="mb-12">
        <Link to="/founder-command/campaigns" className="flex items-center justify-between gap-4 bg-white/70 border border-neutral-200/60 rounded-xl p-5 hover:bg-white transition-colors" data-testid="campaigns-shortcut">
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-[#C8A15A] mb-1">Utility</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-lg text-neutral-900">
              Campaign Builder
            </div>
            <div className="text-[12px] text-neutral-500 mt-1">Generate trackable UTM links + QR codes for every post, email, and speaking event.</div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-600 shrink-0">Open →</div>
        </Link>
      </section>

      {/* Top KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <StatCard testid="an-subs-active" label="Active Subscribers" value={s.active} sub={`${s.total} total · ${s.unsubscribed} unsub`} />
        <StatCard testid="an-subs-week" label="Subs This Week" value={s.this_week} sub={`${s.this_month} this month`} />
        <StatCard testid="an-inquiries" label="Total Inquiries" value={q.contacts_total} sub={`${q.contacts_this_week} this week`} />
        <StatCard testid="an-conversions" label="Total Conversions" value={data.conversions_total} sub="subs + inquiries + waitlist" />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <StatCard testid="an-speaking" label="Speaking Requests" value={q.speaking_total} sub={`${q.speaking_this_week} this week`} />
        <StatCard testid="an-waitlist" label="Waitlist Signups" value={w.total} sub={`${w.this_week} this week`} />
        <StatCard testid="an-editions-sent" label="Editions Sent" value={e.sent} sub={`${e.draft} draft · ${e.total} total`} />
        <StatCard
          testid="an-conversion-rate"
          label="Sub → List Ratio"
          value={`${s.total > 0 ? Math.round((s.active / s.total) * 100) : 0}%`}
          sub="active / total"
        />
      </section>

      {/* Growth chart */}
      <section className="bg-white/70 border border-neutral-200/60 rounded-xl p-6 mb-8">
        <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">Subscriber Growth · Last 8 Weeks</div>
        <div className="text-[13px] text-neutral-500 mb-6">Weekly new signups on The Founder's List.</div>
        <GrowthChart series={s.growth_8w || []} />
      </section>

      {/* Two column: sources + inquiry kinds */}
      <section className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white/70 border border-neutral-200/60 rounded-xl p-6">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">Signup Sources</div>
          <div className="text-[13px] text-neutral-500 mb-4">Where new subscribers are joining from.</div>
          {(s.signup_sources || []).length === 0 && <div className="text-neutral-400 text-[13px] py-4">No signups yet.</div>}
          {(s.signup_sources || []).map((row) => {
            const maxCount = Math.max(1, ...s.signup_sources.map((x) => x.count));
            return <HBar key={row.source} label={row.source} count={row.count} max={maxCount} />;
          })}
        </div>

        <div className="bg-white/70 border border-neutral-200/60 rounded-xl p-6">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">Inquiry Types</div>
          <div className="text-[13px] text-neutral-500 mb-4">Breakdown of all contact form submissions.</div>
          {(q.contacts_by_kind || []).length === 0 && <div className="text-neutral-400 text-[13px] py-4">No inquiries yet.</div>}
          {(q.contacts_by_kind || []).map((row) => {
            const maxCount = Math.max(1, ...q.contacts_by_kind.map((x) => x.count));
            return <HBar key={row.kind} label={row.kind} count={row.count} max={maxCount} />;
          })}
        </div>
      </section>

      {/* Waitlist products */}
      <section className="bg-white/70 border border-neutral-200/60 rounded-xl p-6 mb-8">
        <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">Waitlist by Product</div>
        {(w.by_product || []).length === 0 && <div className="text-neutral-400 text-[13px] py-4">No waitlist signups yet.</div>}
        {(w.by_product || []).map((row) => {
          const maxCount = Math.max(1, ...w.by_product.map((x) => x.count));
          return <HBar key={row.product} label={row.product} count={row.count} max={maxCount} />;
        })}
      </section>

      <p className="text-[11px] text-neutral-400 leading-relaxed max-w-2xl">
        This dashboard tracks first-party data owned by iamtoiawallace.com. To see traffic, sessions, referrers, and geography, use the &ldquo;Open Google Analytics&rdquo; button above. Additional analytics providers can be added in <code>frontend/src/lib/analytics.js</code>.
      </p>
    </div>
  );
}
